import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { syncManager } from '@/lib/sync-manager'

export function OfflineIndicator() {
  const { t } = useTranslation()
  const isOnline = useOnlineStatus()
  const [pendingSync, setPendingSync] = useState(false)
  const [syncStatus, setSyncStatus] = useState<string>('')

  useEffect(() => {
    const checkPending = async () => {
      const hasPending = await syncManager.hasPendingSync()
      setPendingSync(hasPending)
    }

    checkPending()
    const interval = setInterval(checkPending, 10000) // Check every 10s

    // Listen to sync status
    const unsubscribe = syncManager.onSyncStatusChange((status) => {
      setSyncStatus(status)
      if (status === 'success') {
        setPendingSync(false)
        setTimeout(() => setSyncStatus(''), 3000)
      }
    })

    return () => {
      clearInterval(interval)
      unsubscribe()
    }
  }, [])

  const handleSync = async () => {
    await syncManager.sync()
  }

  if (!isOnline) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <div className="bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
            />
          </svg>
          <span className="text-sm font-medium">{t('offline.noConnection')}</span>
        </div>
      </div>
    )
  }

  if (pendingSync) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <div className="bg-yellow-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-3">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="text-sm">{t('offline.pendingSync')}</span>
          <button
            onClick={handleSync}
            className="px-3 py-1 bg-white text-yellow-700 rounded text-sm font-medium hover:bg-yellow-50"
          >
            {syncStatus === 'syncing' ? t('offline.syncing') : t('offline.syncNow')}
          </button>
        </div>
      </div>
    )
  }

  if (syncStatus === 'success') {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <div className="bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm font-medium">{t('offline.syncSuccess')}</span>
        </div>
      </div>
    )
  }

  return null
}
